import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Shield, Users, AlertTriangle, Eye, Clock, RefreshCw, Search,
  ChevronRight, AlertCircle, CheckCircle, XCircle, Monitor,
  Activity, BarChart3, Flag, Loader2, Camera, Play, Pause, Download, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadProctoringReport, generateAIAnalysis } from '../utils/proctoringReportGenerator';

const API = process.env.REACT_APP_BACKEND_URL;

const ProctorDashboard = () => {
  const { challengeId } = useParams();
  const [token] = useState(localStorage.getItem('company_token'));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [analyzingAttempt, setAnalyzingAttempt] = useState(null);
  const [aiAnalysisResults, setAiAnalysisResults] = useState({});
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);
  
  const refreshInterval = useRef(null);

  useEffect(() => {
    fetchChallenge();
    fetchProctorData();
    
    if (autoRefresh) {
      refreshInterval.current = setInterval(fetchProctorData, 10000); // Refresh every 10s
    }
    
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
  }, [challengeId, selectedRound, autoRefresh]);

  const fetchChallenge = async () => {
    try {
      const res = await axios.get(`${API}/api/company/enterprise/challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenge(res.data.challenge);
      if (res.data.challenge.rounds?.length > 0 && !selectedRound) {
        setSelectedRound(res.data.challenge.rounds[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch challenge:', err);
    }
  };

  const fetchProctorData = async () => {
    try {
      const params = selectedRound ? `?round_id=${selectedRound}` : '';
      const res = await axios.get(
        `${API}/api/company/enterprise/challenges/${challengeId}/proctor-dashboard${params}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch proctor data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTerminate = async (attemptId) => {
    if (!window.confirm('Are you sure you want to terminate this participant\'s assessment?')) return;
    
    try {
      await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/terminate-attempt/${attemptId}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { reason: 'Terminated by proctor' }
        }
      );
      toast.success('Assessment terminated');
      fetchProctorData();
    } catch (err) {
      toast.error('Failed to terminate assessment');
    }
  };

  const handleAIAnalyze = async (attemptId) => {
    setAnalyzingAttempt(attemptId);
    try {
      const res = await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/proctoring/analyze-frames/${attemptId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setAiAnalysisResults(prev => ({
        ...prev,
        [attemptId]: res.data
      }));
      
      if (res.data.risk_score >= 70) {
        toast.warning(`High risk detected: ${res.data.analysis?.summary || 'Review required'}`);
      } else if (res.data.analysis) {
        toast.success(`Analysis complete: ${res.data.risk_level} risk`);
      } else {
        toast.info('No frames to analyze');
      }
      
      fetchProctorData(); // Refresh to show updated violations
    } catch (err) {
      toast.error('AI analysis failed');
    } finally {
      setAnalyzingAttempt(null);
    }
  };

  const handleBatchAnalyze = async () => {
    setBatchAnalyzing(true);
    try {
      const params = selectedRound ? `?round_id=${selectedRound}` : '';
      const res = await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/proctoring/batch-analyze${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Batch analysis initiated for ${res.data.results?.length || 0} attempts`);
    } catch (err) {
      toast.error('Batch analysis failed');
    } finally {
      setBatchAnalyzing(false);
    }
  };

  const fetchAIAnalysis = async (attemptId) => {
    try {
      const res = await axios.get(
        `${API}/api/company/enterprise/challenges/${challengeId}/proctoring/ai-analysis/${attemptId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setAiAnalysisResults(prev => ({
        ...prev,
        [attemptId]: { analyses: res.data.analyses, highest_risk_score: res.data.highest_risk_score }
      }));
    } catch (err) {
      console.error('Failed to fetch AI analysis:', err);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-500 bg-red-500/20';
      case 'high': return 'text-orange-500 bg-orange-500/20';
      case 'medium': return 'text-amber-500 bg-amber-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const handleGenerateReport = async (participant) => {
    try {
      // Fetch full participant data
      const res = await axios.get(
        `${API}/api/company/enterprise/challenges/${challengeId}/participant/${participant.attempt_id}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const participantData = res.data;
      
      // Generate AI analysis
      const aiAnalysis = generateAIAnalysis(
        participantData.violations || [],
        participantData.ai_events || [],
        participantData.scores
      );
      
      // Generate and download report
      downloadProctoringReport({
        participant: {
          name: participant.name,
          email: participant.email,
          college: participantData.college,
          phone: participantData.phone
        },
        challenge: {
          title: challenge?.title,
          company: challenge?.company_name
        },
        attempt: {
          id: participant.attempt_id,
          start_time: participantData.start_time,
          end_time: participantData.end_time,
          duration_minutes: participantData.duration_minutes,
          status: participant.status
        },
        scores: {
          total: participantData.total_points || 100,
          achieved: participantData.score || 0,
          percentage: participantData.percentage || 0,
          passingPercentage: 40
        },
        violations: participantData.violations || [],
        aiEvents: participantData.ai_events || [],
        screenshots: participantData.screenshots || [],
        analysis: aiAnalysis
      });
      
      toast.success('Proctoring report downloaded');
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error('Failed to generate report');
    }
  };

  const filteredFlagged = data?.flagged_participants?.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="animate-spin text-cyan-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="bg-[#12121a] border-b border-white/10 px-6 py-4 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/company/enterprise`} className="text-gray-400 hover:text-white">
              <ChevronRight className="rotate-180" size={24} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Shield className="text-cyan-400" />
                Live Proctor Dashboard
              </h1>
              <p className="text-gray-400 text-sm">{challenge?.title}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Round Selector */}
            {challenge?.rounds?.length > 0 && (
              <select
                value={selectedRound || ''}
                onChange={(e) => setSelectedRound(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white"
              >
                {challenge.rounds.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            )}
            
            {/* Auto Refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                autoRefresh ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-gray-400'
              }`}
            >
              {autoRefresh ? <Play size={18} /> : <Pause size={18} />}
              Auto Refresh
            </button>
            
            {/* Manual Refresh */}
            <button
              onClick={fetchProctorData}
              className="p-2 bg-white/5 text-gray-400 rounded-lg hover:bg-white/10 hover:text-white"
            >
              <RefreshCw size={20} />
            </button>
            
            {/* Batch AI Analysis */}
            <button
              onClick={handleBatchAnalyze}
              disabled={batchAnalyzing}
              className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 flex items-center gap-2 disabled:opacity-50"
            >
              {batchAnalyzing ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
              AI Batch Analyze
            </button>
          </div>
        </div>
      </header>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/20 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Live Now</span>
              <Activity className="text-green-400" size={20} />
            </div>
            <div className="text-3xl font-bold text-white">{data?.live_count || 0}</div>
            <div className="text-green-400 text-sm mt-1">Active participants</div>
          </div>
          
          <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 border border-cyan-500/20 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Completed</span>
              <CheckCircle className="text-cyan-400" size={20} />
            </div>
            <div className="text-3xl font-bold text-white">{data?.completed_count || 0}</div>
            <div className="text-cyan-400 text-sm mt-1">Submissions</div>
          </div>
          
          <div className="bg-gradient-to-br from-red-500/20 to-red-500/5 border border-red-500/20 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Terminated</span>
              <XCircle className="text-red-400" size={20} />
            </div>
            <div className="text-3xl font-bold text-white">{data?.terminated_count || 0}</div>
            <div className="text-red-400 text-sm mt-1">Due to violations</div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/20 rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">Flagged</span>
              <Flag className="text-amber-400" size={20} />
            </div>
            <div className="text-3xl font-bold text-white">{data?.total_flagged || 0}</div>
            <div className="text-amber-400 text-sm mt-1">Need attention</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Flagged Participants List */}
          <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-white font-semibold flex items-center gap-2">
                <AlertTriangle className="text-amber-400" size={20} />
                Flagged Participants
              </h2>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white text-sm w-48"
                />
              </div>
            </div>
            
            <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
              {filteredFlagged.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Shield size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No flagged participants</p>
                </div>
              ) : (
                filteredFlagged.map((participant) => (
                  <div
                    key={participant.attempt_id}
                    className={`p-4 hover:bg-white/5 cursor-pointer transition-all ${
                      selectedParticipant?.attempt_id === participant.attempt_id ? 'bg-white/5' : ''
                    }`}
                    onClick={() => setSelectedParticipant(participant)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-medium">{participant.name}</h3>
                          {participant.status === 'terminated' && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                              Terminated
                            </span>
                          )}
                        </div>
                        <p className="text-gray-500 text-sm">{participant.email}</p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          (participant.violation_count + (participant.ai_violation_count || 0)) >= 7 ? 'bg-red-500/20 text-red-400' :
                          (participant.violation_count + (participant.ai_violation_count || 0)) >= 4 ? 'bg-amber-500/20 text-amber-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {participant.violation_count + (participant.ai_violation_count || 0)} violations
                        </div>
                        
                        {participant.ai_violation_count > 0 && (
                          <div className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                            <Camera size={12} />
                            {participant.ai_violation_count} AI
                          </div>
                        )}
                        
                        {participant.status !== 'terminated' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTerminate(participant.attempt_id);
                            }}
                            className="px-3 py-1 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30"
                          >
                            Terminate
                          </button>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateReport(participant);
                          }}
                          className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/30 flex items-center gap-1"
                          title="Download Proctoring Report"
                        >
                          <Download size={14} />
                          Report
                        </button>
                        
                        {/* AI Analysis Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAIAnalyze(participant.attempt_id);
                          }}
                          disabled={analyzingAttempt === participant.attempt_id}
                          className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 flex items-center gap-1 disabled:opacity-50"
                          title="Run AI Analysis"
                        >
                          {analyzingAttempt === participant.attempt_id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Eye size={14} />
                          )}
                          AI Scan
                        </button>
                      </div>
                    </div>
                    
                    {/* Last AI Event Preview */}
                    {participant.last_ai_event && (
                      <div className="mt-2 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-center gap-2 text-sm">
                        <Camera size={14} className="text-purple-400" />
                        <span className="text-purple-400">AI:</span>
                        <span className="text-gray-300">{participant.last_ai_event.event_type?.replace('_', ' ')}</span>
                        <span className="text-gray-500 text-xs ml-auto">
                          {new Date(participant.last_ai_event.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    )}
                    
                    {/* Last Violation Preview */}
                    {participant.last_violation && (
                      <div className="mt-2 p-2 bg-white/5 rounded-lg flex items-center gap-2 text-sm">
                        <span className={`px-2 py-0.5 rounded ${getSeverityColor(participant.last_violation.severity)}`}>
                          {participant.last_violation.severity}
                        </span>
                        <span className="text-gray-400">{participant.last_violation.type}:</span>
                        <span className="text-gray-300">{participant.last_violation.details}</span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Violation Details Panel */}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h2 className="text-white font-semibold flex items-center gap-2">
                <Eye className="text-purple-400" size={20} />
                Violation Details
              </h2>
            </div>
            
            {selectedParticipant ? (
              <div className="p-4">
                <div className="mb-4">
                  <h3 className="text-white font-medium">{selectedParticipant.name}</h3>
                  <p className="text-gray-500 text-sm">{selectedParticipant.email}</p>
                </div>
                
                <div className="mb-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">System Violations</span>
                    <span className="text-white font-medium">{selectedParticipant.violation_count}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Camera size={12} />
                      AI Violations
                    </span>
                    <span className="text-purple-400 font-medium">{selectedParticipant.ai_violation_count || 0}</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        (selectedParticipant.violation_count + (selectedParticipant.ai_violation_count || 0)) >= 7 ? 'bg-red-500' :
                        (selectedParticipant.violation_count + (selectedParticipant.ai_violation_count || 0)) >= 4 ? 'bg-amber-500' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(100, (selectedParticipant.violation_count + (selectedParticipant.ai_violation_count || 0)) * 10)}%` }}
                    />
                  </div>
                </div>
                
                {/* Violation Timeline */}
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {selectedParticipant.last_violation && (
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(selectedParticipant.last_violation.severity)}`}>
                          {selectedParticipant.last_violation.severity}
                        </span>
                        <span className="text-gray-500 text-xs">
                          {new Date(selectedParticipant.last_violation.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-white text-sm">{selectedParticipant.last_violation.type}</p>
                      <p className="text-gray-400 text-xs mt-1">{selectedParticipant.last_violation.details}</p>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                {selectedParticipant.status !== 'terminated' && (
                  <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                    <button
                      onClick={() => handleAIAnalyze(selectedParticipant.attempt_id)}
                      disabled={analyzingAttempt === selectedParticipant.attempt_id}
                      className="w-full py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {analyzingAttempt === selectedParticipant.attempt_id ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Eye size={18} />
                          Run AI Analysis
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleTerminate(selectedParticipant.attempt_id)}
                      className="w-full py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600"
                    >
                      Terminate Assessment
                    </button>
                  </div>
                )}
                
                {/* AI Analysis Results */}
                {aiAnalysisResults[selectedParticipant.attempt_id] && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <Eye className="text-purple-400" size={16} />
                      AI Analysis Result
                    </h4>
                    
                    {aiAnalysisResults[selectedParticipant.attempt_id].analysis && (
                      <div className="space-y-3">
                        {/* Risk Score */}
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Risk Score</span>
                          <span className={`px-3 py-1 rounded-full font-bold ${
                            aiAnalysisResults[selectedParticipant.attempt_id].risk_score >= 70 ? 'bg-red-500/20 text-red-400' :
                            aiAnalysisResults[selectedParticipant.attempt_id].risk_score >= 40 ? 'bg-amber-500/20 text-amber-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {aiAnalysisResults[selectedParticipant.attempt_id].risk_score}/100
                          </span>
                        </div>
                        
                        {/* Summary */}
                        <div className="p-3 bg-white/5 rounded-lg">
                          <p className="text-sm text-gray-300">
                            {aiAnalysisResults[selectedParticipant.attempt_id].analysis?.summary || 'No issues detected'}
                          </p>
                        </div>
                        
                        {/* Violations */}
                        {aiAnalysisResults[selectedParticipant.attempt_id].analysis?.violations?.length > 0 && (
                          <div className="space-y-2">
                            <span className="text-gray-400 text-sm">Detected Issues:</span>
                            {aiAnalysisResults[selectedParticipant.attempt_id].analysis.violations.map((v, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <span className={`w-2 h-2 rounded-full ${
                                  v.confidence === 'high' ? 'bg-red-500' :
                                  v.confidence === 'medium' ? 'bg-amber-500' :
                                  'bg-gray-500'
                                }`} />
                                <span className="text-gray-300">{v.type?.replace(/_/g, ' ')}</span>
                                <span className="text-gray-500 text-xs">({v.confidence})</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Recommendation */}
                        <div className={`p-2 rounded-lg text-sm font-medium text-center ${
                          aiAnalysisResults[selectedParticipant.attempt_id].analysis?.recommendation === 'terminate' ? 'bg-red-500/20 text-red-400' :
                          aiAnalysisResults[selectedParticipant.attempt_id].analysis?.recommendation === 'review' ? 'bg-amber-500/20 text-amber-400' :
                          aiAnalysisResults[selectedParticipant.attempt_id].analysis?.recommendation === 'flag' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          Recommendation: {aiAnalysisResults[selectedParticipant.attempt_id].analysis?.recommendation?.toUpperCase() || 'ALLOW'}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Monitor size={48} className="mx-auto mb-3 opacity-50" />
                <p>Select a participant to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProctorDashboard;
